import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { Users, TrendingUp, Sparkles, MapPin } from 'lucide-react';
export function LaosMarketSection() {
  const {
    t
  } = useLanguage();
  const stats = [{
    icon: <Users className="w-6 h-6 text-brand-copper" data-id="element-553" />,
    value: '7.5M',
    label: t('market.totalPop')
  }, {
    icon: <Users className="w-6 h-6 text-brand-copper" data-id="element-554" />,
    value: '3.75M',
    label: t('market.femalePop')
  }, {
    icon: <Sparkles className="w-6 h-6 text-brand-copper" data-id="element-555" />,
    value: '1.6M',
    label: t('market.targetDemo')
  }, {
    icon: <TrendingUp className="w-6 h-6 text-brand-copper" data-id="element-556" />,
    value: '450K+',
    label: t('market.beautyConscious')
  }];
  const regions = [{
    id: 'north',
    name: t('market.north'),
    pop: '120K+',
    top: '25%',
    left: '35%'
  }, {
    id: 'central',
    name: t('market.central'),
    pop: '250K+',
    top: '50%',
    left: '45%'
  }, {
    id: 'south',
    name: t('market.south'),
    pop: '80K+',
    top: '75%',
    left: '65%'
  }];
  return <section className="py-24 bg-brand-bg overflow-hidden relative" data-id="element-557">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-558">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-lightCopper/20 rounded-full blur-3xl" data-id="element-559"></div>
        <div className="absolute top-1/2 -left-20 w-72 h-72 bg-brand-copper/10 rounded-full blur-3xl" data-id="element-560"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-561">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-562">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-sm font-bold tracking-widest text-brand-copper uppercase mb-3" data-id="element-563">
            {t('market.label')}
          </motion.h2>
          <motion.h3 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-4xl font-light text-gray-900 mb-6" data-id="element-564">
            {t('market.title1')}{' '}
            <span className="font-semibold" data-id="element-565">{t('market.title2')}</span>
          </motion.h3>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-gray-600 leading-relaxed" data-id="element-566">
            {t('market.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-567">
          {/* Left: Stats Grid */}
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-568">
            {stats.map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-569">
                <div className="w-12 h-12 bg-brand-lightCopper/20 rounded-xl flex items-center justify-center mb-4" data-id="element-570">
                  {stat.icon}
                </div>
                <h4 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-571">
                  {stat.value}
                </h4>
                <p className="text-sm text-gray-500 font-medium" data-id="element-572">
                  {stat.label}
                </p>
              </motion.div>)}

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="sm:col-span-2 bg-gradient-to-br from-brand-copper to-brand-darkCopper p-8 rounded-2xl text-white shadow-lg relative overflow-hidden" data-id="element-573">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl" data-id="element-574"></div>
              <h4 className="text-lg font-medium mb-2 opacity-90" data-id="element-575">
                {t('market.growth')}
              </h4>
              <div className="flex items-end gap-3" data-id="element-576">
                <span className="text-5xl font-bold" data-id="element-577">15%</span>
                <span className="text-sm mb-1 opacity-80" data-id="element-578">
                  CAGR (2024-2028)
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right: 3D-style Map */}
          <div className="lg:col-span-7 relative" data-id="element-579">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="relative w-full aspect-[4/5] max-w-lg mx-auto" data-id="element-580">
              {/* Stylized SVG Map of Laos */}
              <div className="absolute inset-0 drop-shadow-[0_20px_30px_rgba(40,99,147,0.3)]" data-id="element-581">
                <svg viewBox="0 0 400 500" className="w-full h-full" style={{
                filter: 'drop-shadow(0px 10px 15px rgba(0,0,0,0.1))'
              }} data-id="element-582">
                  <defs data-id="element-583">
                    <linearGradient id="mapGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-584">
                      <stop offset="0%" stopColor="#C8DCE9" data-id="element-585" />
                      <stop offset="50%" stopColor="#286393" data-id="element-586" />
                      <stop offset="100%" stopColor="#1D4A6E" data-id="element-587" />
                    </linearGradient>
                    <filter id="innerShadow" data-id="element-588">
                      <feOffset dx="0" dy="4" data-id="element-589" />
                      <feGaussianBlur stdDeviation="5" result="offset-blur" data-id="element-590" />
                      <feComposite operator="out" in="SourceGraphic" in2="offset-blur" result="inverse" data-id="element-591" />
                      <feFlood floodColor="black" floodOpacity="0.2" result="color" data-id="element-592" />
                      <feComposite operator="in" in="color" in2="inverse" result="shadow" data-id="element-593" />
                      <feComposite operator="over" in="shadow" in2="SourceGraphic" data-id="element-594" />
                    </filter>
                  </defs>

                  {/* Simplified path of Laos */}
                  <path d="M140,50 C160,40 180,60 190,80 C200,100 180,120 170,140 C160,160 180,180 200,190 C220,200 240,210 250,230 C260,250 280,270 290,290 C300,310 320,330 310,350 C300,370 280,390 270,410 C260,430 240,450 220,440 C200,430 180,410 190,390 C200,370 180,350 170,330 C160,310 140,290 130,270 C120,250 100,230 110,210 C120,190 100,170 90,150 C80,130 100,110 110,90 C120,70 120,60 140,50 Z" fill="url(#mapGradient)" stroke="#ffffff" strokeWidth="2" filter="url(#innerShadow)" className="transition-all duration-500 hover:brightness-110" data-id="element-595" />
                </svg>
              </div>

              {/* Region Indicators */}
              {regions.map((region, index) => <motion.div key={region.id} initial={{
              opacity: 0,
              scale: 0
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5 + index * 0.2,
              type: 'spring'
            }} className="absolute flex flex-col items-center group cursor-pointer" style={{
              top: region.top,
              left: region.left
            }} data-id="element-596">
                  <div className="relative" data-id="element-597">
                    <div className="w-4 h-4 bg-white rounded-full shadow-lg z-10 relative border-4 border-brand-copper" data-id="element-598"></div>
                    <div className="absolute inset-0 bg-brand-copper rounded-full animate-ping opacity-75" data-id="element-599"></div>
                  </div>

                  <div className="mt-2 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg border border-brand-lightCopper/30 text-center transform transition-transform group-hover:-translate-y-1 group-hover:scale-105 min-w-[120px]" data-id="element-600">
                    <p className="text-xs font-bold text-gray-900 mb-0.5" data-id="element-601">
                      {region.name}
                    </p>
                    <p className="text-[10px] text-brand-copper font-semibold uppercase tracking-wider" data-id="element-602">
                      {region.pop} Beauty Users
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}